import dayjs from 'dayjs';
import { Dispatch, FC, ReactNode, RefObject, SetStateAction } from 'react';
import { ISlot, SlotTypeEnum } from '../organisms/bo_calendar_slot/types';
import { SerializedStyles } from '@emotion/react';
import { ResourceTypeEnum } from '../types/enums/resource';
import { IColors } from '../types';
import { ResourceNameTypeEnum } from '../types/enums';

export { SlotTypeEnum };

export interface IResource {
	id: number;
	name: string;
	parentSpaceId?: number;
	organizationId: number;
	children: IResource[];
	activityTimes: IActivityTime[];
	resourceType: ResourceTypeEnum;
}

export interface IReservations {
	creatorType: string;
	name: string;
}

export enum ECalendarMode {
	DAILY = 'daily',
	WEEKLY = 'weekly',
	MONTHLY = 'monthly',
	DAYX = 'dayx',
}

export enum ECalendarView {
	VERTICAL = 'vertical',
	HORIZONTAL = 'horizontal',
	LIST = 'list',
}

export type DraggingOptions = Record<ResourceNameTypeEnum, boolean>;

export interface IOptions {
	dragAndDrop?: boolean;
	resize?: SlotTypeEnum[];
	infiniteScrolling?: boolean;
	mode?: ECalendarMode;
	view?: ECalendarView;
	date?: string;
	partialRendering?: boolean;
	isSundayFirstDay?: boolean;
	dndStepLength?: number;
	/** Visual gridline spacing in minutes; omit or 0 for none. Independent of DnD snap. */
	gridLineStepMinutes?: number;
	hourSize?: {
		vertical?: number;
		horizontal?: number;
	};
	resourceSize?: {
		width?: number;
		height?: number;
	};
	addNewSlotButton?: {
		hoverDelay?: string;
		duration?: number;
		step?: number;
	};
	scrollTo?: {
		startTime?: string;
		endTime?: string;
		resourceId?: string | number;
		trigger?: number;
	};
	monthlySpecificMonth?: boolean;
	isLoading?: boolean;
	scrollPosition?: {
		scrollTop: number;
		scrollLeft: number;
	};
	draggingOptions?: DraggingOptions;
	dayXDaysCount?: number;
}

export interface ICalendarProps {
	//key?: string;
	resources: IResource[];
	widthChangeTrigger?: boolean;
	slots: Record<number, ISlot[]>;
	EventComponent: FC<IEventComponentProps>;
	DragOverlayComponent: FC<IDragOverlayComponentProps>;
	ResourceGroupHeader: FC<ISpaceProps>;
	onChange: TOnChangeInCalender;
	options: IOptions;
	resourceId?: string | number;
	isLoading?: boolean;
	groups: any;
	//resources: any[];
	view: ECalendarView;
}

export type TOnChangeInCalender = (data: CalendarEvents) => void | boolean;

export interface IEventComponentProps {
	event: ISlot;
	horizontal?: boolean;
	currentDate?: string;
	mode?: ECalendarMode;
	inList?: boolean;
}

export interface IDragOverlayComponentProps {
	event: ISlot;
	hours?: string;
	currentDate: string;
	horizontal?: boolean;
	mode: ECalendarMode;
}

export interface ISlotsEventsAndEventsWithSpaces {
	eventsToSpaces: ISlotEventsToSpaces;
	events: ISlotsEvents;
}

export interface ISlotsEvents {
	[eventId: number]: ISlot;
}

export interface ISlotEventsToSpaces {
	[eventId: number]: ISlot[];
}

export interface IDndProviderProps {
	children: ReactNode;
	DragOverlayComponent: FC<IDragOverlayComponentProps>;
	onChange?: TOnChangeInCalender;
	horizontal?: boolean;
	view?: ECalendarView;
}

export interface IDayToDropProps {
	id: string;
	EventComponent: FC<IEventComponentProps>;
	withoutChildren?: boolean;
	hiddenEvents?: boolean;
	onAddNewSlotClick?: TOnChangeInCalender;
	isModeChanged: boolean;
	currentDate: string;
	isLoading?: boolean;
	activityTimes: IActivityTime[];
	updateSlotData?: (id: number, updatedSlot: Partial<ISlot>, data?: any) => void;
	slots?: ISlot[];
}

export interface IDayXToDropProps extends IDayToDropProps {
	updateSlotData: (id: number, updatedSlot: Partial<ISlot>, data?: any) => void;
	slots: ISlot[];
}

export interface IWeekToDropProps {
	resourceId: string;
	EventComponent: FC<IEventComponentProps>;
	isLoading?: boolean;
}

export interface ISpaceProps {
	state: IResource;
	view?: any;
	mode?: any;
	resources: any[];
	selectedMonthlyResource: any;
	groups: any;
	handleSetSelectedMonthlyResource?: (value: number) => void;
	selectResourceGroup?: (id: number, childrenSlotIds: number[]) => void;
	resourceGroups?: Record<number, number[]> | null;
}

export interface IDraggableProps {
	item: ISlot | undefined;
	delta?: number;
	Component: FC<IDragOverlayComponentProps>;
	view: ECalendarView;
	mode: ECalendarMode;
}

export interface IDroppableProps {
	id: string;
	dragging?: boolean;
	weekly?: boolean;
	height?: number | string;
	maxWidth?: number | string;
}

export interface IDraggableDayToDropProps {
	id: number | string;
	state: ISlot;
	parentId: number | string;
	groupTop?: number;
	groupCount?: number;
	groupIndex?: number;
	isEditable?: boolean;
	isConnectedMaintenance?: boolean;
	currentDate: string;
	setDraggableSlot: Dispatch<SetStateAction<ISlot>>;
	updateSlotData: (id: number, updatedSlot: Partial<ISlot>, data: any) => void;
}

export interface ClientXAndClientY {
	clientX: number;
	clientY: number;
}

export interface IDailyViewProps {
	EventComponent: FC<IEventComponentProps>;
	onAddNewSlotClick?: TOnChangeInCalender;
	isModeChanged: boolean;
	hourSize?: number;
	isLoading?: boolean;
	currentDate: string;
	resourceGroups: Record<number, number[]> | null;
}

export interface ISlotsGroup {
	items: ISlot[];
	item?: ISlot;
	groups?: ISlot[][];
	height?: number;
	top?: number;
}

export interface IPartialRange {
	from: number;
	to: number;
}

export interface IPartialRanges {
	vertical: IPartialRange;
	horizontal: IPartialRange;
}

export type ElementHTML = HTMLElement | null | undefined;

export interface IAsideContainerProps {
	width: number;
	bodyContainerHeight: number;
	verticalSpacesRef: RefObject<HTMLDivElement>;
	ResourceGroupHeader: FC<ISpaceProps>;
	groups: any;
	view: ECalendarView;
	mode: ECalendarMode;
	selectedMonthlyResource: number;
	resourceGroups: Record<number, number[]> | null;
	selectResourceGroup: (id: number, childrenSlotIds: number[]) => void;
}

export interface IHeaderContainerProps {
	width: number;
	headerRef: RefObject<HTMLDivElement>;
	ResourceGroupHeader: FC<ISpaceProps>;
	resourceId?: string | number;
	groups: any;
	mode: ECalendarMode;
	view: ECalendarView;
	selectResourceGroup: (id: number, childrenSlotIds: number[]) => void;
	resourceGroups: Record<number, number[]> | null;
	handleSetSelectedMonthlyResource: (value: number) => void;
}

export interface ITimeLineProps {
	width: number;
	view: ECalendarView;
}

export interface IAddNewSlotButtonsContainerProps {
	onClick?: TOnChangeInCalender;
	horizontal?: boolean;
	parentId: string;
	activityTimes: IActivityTime[];
	hourSize: number;
	date: string;
}

export interface IAddNewSlotButtonProps {
	index: number;
	parentId: string;
	size: number;
	duration: number;
	horizontal?: boolean;
	onClick?: TOnChangeInCalender;
	customCss?: SerializedStyles;
	colors: IColors;
	date: string;
}

export interface IPlusButtonData {
	customLabel?: string;
	height: number;
	top: number;
	bottom: number;
	isEvent?: boolean;
	isGroup?: true;
}

export interface IHourBoxProps {
	date: dayjs.Dayjs;
	width: number;
}

export interface IMonthlyHeaderContainerProps {
	width: number;
	headerRef: RefObject<HTMLDivElement>;
	resource: IResource;
	ResourceGroupHeader: FC<ISpaceProps>;
	resourceId?: string | number;
	groups: any;
	resources: any[];
	mode: any;
	view: any;
	handleSetSelectedMonthlyResource: (value: number) => void;
}

export interface IWeekBoxProps {
	weekNumber: number;
	width: number;
	horizontal?: boolean;
}

export interface IWeeklyDraggableProps {
	id: number;
	state: ISlot;
	parentId: number | string;
}

export interface IWeeklyViewProps {
	EventComponent: FC<IEventComponentProps>;
	isLoading?: boolean;
	resourceGroups: Record<number, number[]> | null;
}

export interface IUsePartialRenderProps {
	ref?: RefObject<HTMLDivElement>;
	elementSize: number;
	direction?: 'vertical' | 'horizontal';
	disable?: boolean;
}

export interface IResizeOptions {
	step?: number;
	axis?: 'vertical' | 'horizontal' | 'both';
}

export const SCROLL_TO_DRAFT_SLOT = 'scroll-to-draft';

export interface IScrollTo {
	startTime: string;
	endTime: string;
	resourceId: number;
}

export interface IActivityTime {
	open: string;
	close: string;
}

export interface ITimeRange {
	start: string;
	end: string;
}

export interface ITimeRangeMinutes {
	start: number;
	end: number;
}

export interface ISlotDateGroup {
	date: dayjs.Dayjs;
	slots: ISlot[];
}

export type SlotUpdateDetails = {
	startDate: string;
	endDate: string;
	startTime: string;
	endTime: string;
	overId: string;
	parentId: string;
	resourceId: string;
	slotId: string;
	differenceInMinutes: number;
};

export type OnSlotDrop = {
	type: 'SLOT_DROP';
	data: SlotUpdateDetails;
};

export type OnSlotResize = {
	type: 'SLOT_RESIZE';
	data: SlotUpdateDetails;
};

export type OnNewBooking = {
	type: 'NEW_BOOKING';
	data: {
		parentId: string;
		startTime: string;
		endTime: string;
		date: string;
	};
};

export type OnNewMode = {
	type: 'NEW_MODE';
	data: any;
};

export type CalendarEvents = OnSlotDrop | OnSlotResize | OnNewBooking | OnNewMode;

export type HandleDropResult = {
	startDate: string;
	endDate: string;
	minutesDifference?: number;
};
